'use client';
/* newlines-between doesn't seem to like the inline requires */
/* related bug: https://github.com/benmosher/eslint-plugin-import/issues/1936 */
/* eslint import/order: ['error', { groups: ['builtin', 'external', 'parent', 'sibling', 'index'], 'newlines-between': 'ignore'}] */
import React from 'react';
import PropTypes from 'prop-types';
import {
  createGlobalStyle,
  ThemeProvider,
  css,
  StyleSheetManager,
} from 'styled-components';
import { get } from 'lodash';

export const GlobalStyle = createGlobalStyle`
  @font-face {
    font-display: swap;
    font-family: 'ABC Social';
    font-weight: normal;
    font-style: normal;
    src: url(${require('../public/fonts/ABCSocial-Regular.woff2')}) format('woff2');
  }

  @font-face {
    font-display: swap;
    font-family: 'ABC Social';
    font-weight: bold;
    font-style: normal;
    src: url(${require('../public/fonts/ABCSocial-Bold.woff2')}) format('woff2');
  }

  @font-face {
    font-display: swap;
    font-family: 'ABC Social Condensed';
    font-weight: normal;
    font-style: normal;
    src: url(${require('../public/fonts/ABCSocialCondensed-Regular.woff2')}) format('woff2');
  }

  @font-face {
    font-display: swap;
    font-family: 'ABC Social Condensed';
    font-weight: medium;
    font-style: normal;
    src: url(${require('../public/fonts/ABCSocialCondensed-Medium.woff2')}) format('woff2');
  }

  @font-face {
    font-display: swap;
    font-family: 'ABC Social Condensed';
    font-weight: bold;
    font-style: normal;
    src: url(${require('../public/fonts/ABCSocialCondensed-Black.woff2')}) format('woff2');
  }

  @font-face {
    font-display: swap;
    font-family: 'ABC Social Mono';
    font-weight: normal;
    font-style: normal;
    src: url(${require('../public/fonts/ABCSocialMono-Regular.woff2')}) format('woff2');
  }

  @font-face {
    font-display: swap;
    font-family: 'New Spirit Condensed';
    font-weight: normal;
    font-style: normal;
    src: url(${require('../public/fonts/NewSpiritCondensed-Regular.woff2')}) format('woff2');
  }

  @font-face {
    font-display: swap;
    font-family: 'New Spirit Condensed';
    font-weight: bold;
    font-style: normal;
    src: url(${require('../public/fonts/NewSpiritCondensed-Bold.woff2')}) format('woff2');
  }

  @font-face {
    font-display: swap;
    font-family: 'New Spirit Condensed';
    font-weight: light;
    font-style: normal;
    src: url(${require('../public/fonts/NewSpiritCondensed-Light.woff2')}) format('woff2');
  }

  @font-face {
    font-display: swap;
    font-family: 'New Spirit Condensed';
    font-weight: medium;
    font-style: normal;
    src: url(${require('../public/fonts/NewSpiritCondensed-Medium.woff2')}) format('woff2');
  }

  @font-face {
    font-display: swap;
    font-family: 'New Spirit Condensed';
    font-weight: 600;
    font-style: normal;
    src: url(${require('../public/fonts/NewSpiritCondensed-SemiBold.woff2')}) format('woff2');
  }

  @font-face {
    font-family: 'Adjusted Arial Fallback';
    src: local(Arial);
    /* stylelint-disable-next-line */
    size-adjust: 0%;
    ascent-override: 100%;
    descent-override: normal;
    line-gap-override: normal;
  }

@font-face {
    font-family: 'Adjusted Arial Narrow Fallback';
    src: local(Arial Narrow);
    size-adjust: 104%;
    ascent-override: normal;
    descent-override: normal;
    line-gap-override: normal;
}

@font-face {
    font-family: 'Adjusted Tahoma Fallback';
    src: local(Tahoma);
    size-adjust: 85%;
    ascent-override: normal;
    descent-override: normal;
    line-gap-override: 27%;
}

@font-face {
    font-family: 'Adjusted Courier New Fallback';
    src: local(Courier New);
    size-adjust: 96%;
     ascent-override: 80%;
    descent-override: 27%;
    line-gap-override: 25%;
}

  * > * {
    box-sizing: border-box;
  }

  body {
    ${props => props.theme.fontFamily.base}
    color: ${props => props.theme.colors.text.primary};
    background-color: ${props => props.theme.colors.background.base};
    margin: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    * {
      margin: 0;
    }
    img {
      max-width: 100%;
    }

    // There's a tracking .gif with a height & width
    // that also contributes to the white bar (see below).
    > img[height="1"][width="1"] {
      position: fixed;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: bold;
  }

  a {
    text-decoration: none;
  }

  input, select, button {
    ${props => props.theme.fontFamily.base}
  }

  ::-moz-focus-inner {
    border: 0;
  }

  :focus {
    outline: none;
  }

  button {
    cursor: pointer;
    &:disabled {
      cursor: auto;
    }
  }

  #gladlyChat_container .minimizedChatButton.minimizedChatButton,
  #gladlyChat_container .minimizedChatButtonSelector.minimizedChatButtonSelector {
    display: none !important;
  }

  // There's some margin on the element Gladly injects
  // that makes a little white bar appear at the bottom of the page.
  #gladlyChat_container#gladlyChat_container {
    margin: 0 !important;
  }
`;

// Screen sizes
export const size = {
  mobileS: '320px',
  mobileM: '375px',
  mobileL: '425px',
  tabletS: '600px',
  tablet: '768px',
  tabletL: '960px',
  laptop: '1024px',
  laptopL: '1440px',
  desktop: '2560px',
};

/**
 * @type {import('styled-components').DefaultTheme}
 */
export const theme = {
  colors: {
    text: {
      primary: '#2C2A26',
      secondary: '#595954',
      tertiary: '#72726E',
      lighter: '#F1CCFF',
      lightest: '#FFFFFF',
    },
    background: {
      base: '#FFFFFF',
      lightest: '#F3F4F6',
      dark: '#4C0F51',
      disabled: '#E6E6E6',
      checked: 'rgba(241, 204, 255, 0.25)',
    },
    border: {
      light: '#F1CCFF',
      fur: '#F1CCFF' /*To do: cleanup - remove after launch*/,
      dark: '#4C0F51',
    },
    neutral: {
      dark: '#4C0F51',
      darkest: '#2C2A26',
      cream: '#FEFFDF',
      light: '#F3F4F6',
      white: '#FFFFFF',
      darkestFur: '#4C0F51' /*To do: cleanup - remove after launch*/,
      darkerFur: '#2C2A26' /*To do: cleanup - remove after launch*/,
      bone: '#FEFFDF' /*To do: cleanup - remove after launch*/,
      lightBone: '#F3F4F6' /*To do: cleanup - remove after launch*/,
    },
    hero: {
      lilac: '#F1CCFF',
      red: '#DD3400',
      plum: '#4C0F51',
      sweetPotato: '#F1CCFF' /*To do: cleanup - remove after launch*/,
      spinach: '#F1CCFF' /*To do: cleanup - remove after launch*/,
      cranberry: '#DD3400' /*To do: cleanup - remove after launch*/,
      blueberry: '#4C0F51' /*To do: cleanup - remove after launch*/,
      huckleberry: '#4C0F51' /*To do: cleanup - remove after launch*/,
      watermelon: '#F1CCFF' /*To do: cleanup - remove after launch*/,
    },
    secondary: {
      lilac: '#F1CCFF',
      red: '#DD3400',
      plum: '#4C0F51',
      yellow: '#F5FA67',
      cream: '#FEFFDF',
      kale: '#4C0F51' /*To do: cleanup - remove after launch*/,
      egg: '#F5FA67' /*To do: cleanup - remove after launch*/,
      carrot: '#F5FA67' /*To do: cleanup - remove after launch*/,
      chia: '#4C0F51' /*To do: cleanup - remove after launch*/,
      mixedBerry: '#F1CCFF' /*To do: cleanup - remove after launch*/,
      yam: '#4C0F51' /*To do: cleanup - remove after launch*/,
      sea: '#FEFFDF' /*To do: cleanup - remove after launch*/,
      pallette: '#4C0F51' /*To do: cleanup - remove after launch*/,
    },
    status: {
      errorPrimary: ' #DD3400',
      errorLight: '#FBF1F2',
      successPrimary: '#166A27',
      successLight: '#F1F7F4',
      warningPrimary: '#C48321',
      warningLight: '#FEF9F2',
      warningDark: '#A5670A',
    },
    withAlpha(path, alpha, defaultColor) {
      const hexColor = this.get(path, defaultColor);
      let r = 0,
        g = 0,
        b = 0;

      // 3 digits, #ABC
      if (hexColor.length === 4) {
        r = '0x' + hexColor[1] + hexColor[1];
        g = '0x' + hexColor[2] + hexColor[2];
        b = '0x' + hexColor[3] + hexColor[3];

        // 6 digits, #AABBCC
      } else if (hexColor.length === 7) {
        r = '0x' + hexColor[1] + hexColor[2];
        g = '0x' + hexColor[3] + hexColor[4];
        b = '0x' + hexColor[5] + hexColor[6];
      }

      return `rgb(${+r}, ${+g}, ${+b}, ${alpha})`;
    },
    get(path, defaultColor) {
      const color = get(theme.colors, path, defaultColor);

      if (!color) {
        throw new TypeError(`${path} does not exist on theme.colors.`);
      }

      return color;
    },
    shadowGrey: 'rgba(0,0,0,0.25)',
  },

  transparent: 'transparent',
  mobileS: `(min-width: ${size.mobileS})`,
  mobileM: `(min-width: ${size.mobileM})`,
  mobileL: `(min-width: ${size.mobileL})`,
  tabletOnly: `(min-width: ${size.tabletS}) and (max-width: ${size.laptop})`,
  tabletS: `(min-width: ${size.tabletS})`,
  tablet: `(min-width: ${size.tablet})`,
  tabletL: `(min-width: ${size.tabletL})`,
  laptop: `(min-width: ${size.laptop})`,
  laptopL: `(min-width: ${size.laptopL})`,
  desktop: `(min-width: ${size.desktop})`,
  desktopL: `(min-width: ${size.desktop})`,

  scrollBar: css`
    overflow-y: auto;
    scroll-margin-right: ${props => props.theme.spacing(-2)};

    /* This is disabled due to a problem with a :focus elsewhere */
    /* stylelint-disable  no-descending-specificity */
    & ::-webkit-scrollbar-track {
      border-radius: ${props => props.theme.spacing(10)};
      background-color: ${props => props.theme.colors.text.secondary};
    }

    & ::-webkit-scrollbar {
      width: ${props => props.theme.spacing(2)};
    }

    & ::-webkit-scrollbar-thumb {
      border-radius: ${props => props.theme.spacing(10)};
      background-color: ${props => props.theme.colors.secondary.yellow};
    }

    scrollbar-track-color: ${props => props.theme.colors.text.secondary};
    scrollbar-base-color: ${props => props.theme.colors.secondary.yellow};
    /* stylelint-enable */
  `,

  spacing: (...args) => {
    let spacingString = args.map(spacingValue => {
      if (typeof spacingValue === 'number') {
        return `${4 * spacingValue}px`;
      }
      return spacingValue;
    });
    return spacingString.join(' ');
  }, //end of spacing string

  calcGridColumn: value => {
    const columValue = value ? `span ${value}` : 'unset';
    return `grid-column: ${columValue};`;
  },

  getContrastColors: value => {
    switch (value) {
      case 'background.dark':
        return css`
          background-color: ${({ theme }) => theme.colors.background.dark};
          color: ${({ theme }) => theme.colors.text.lightest};
        `;
      case 'background.base':
        return css`
          background-color: ${({ theme }) => theme.colors.background.base};
        `;
      case 'background.lightest':
        return css`
          background-color: ${({ theme }) => theme.colors.background.lightest};
          color: ${({ theme }) => theme.colors.text.secondary};
        `;

      case 'light':
        return css`
          background-color: ${({ theme }) => theme.colors.neutral.cream};
          color: ${({ theme }) => theme.colors.text.primary};
        `;
      case 'dark':
        return css`
          background-color: ${({ theme }) => theme.colors.background.dark};
          color: ${({ theme }) => theme.colors.text.lightest};
        `;
      case 'lilac':
        return css`
          background-color: ${({ theme }) => theme.colors.hero.lilac};
          color: ${({ theme }) => theme.colors.hero.plum};
        `;
      case 'yellow':
        return css`
          background-color: ${({ theme }) => theme.colors.secondary.yellow};
          color: ${({ theme }) => theme.colors.hero.plum};
        `;
      case 'warning':
        return css`
          background-color: ${({ theme }) =>
            theme.colors.status.warningPrimary};
          color: ${({ theme }) => theme.colors.text.lightest};
        `;
      case 'success':
        return css`
          background-color: ${({ theme }) =>
            theme.colors.status.successPrimary};
          color: ${({ theme }) => theme.colors.text.lightest};
        `;
      default:
        return css`
          background-color: ${({ theme }) => theme.colors.background.dark};
          color: ${({ theme }) => theme.colors.text.lightest};
        `;
    }
  },

  getFocusOutline: (parentColor = 'neutral.cream') => {
    const getOutlineColor = parentColor => {
      switch (parentColor) {
        case 'neutral.dark':
        case 'secondary.plum':
          return theme.colors.neutral.light;
        default:
          return theme.colors.neutral.darkest;
      }
    };

    return css`
      &:focus,
      &:active,
      &:focus-within {
        border-radius: ${({ theme }) => theme.spacing(2)};
        outline: 2px solid ${() => getOutlineColor(parentColor)};
        outline-offset: ${({ theme }) => theme.spacing(0.5)};
      }
    `;
  },

  fontFamily: {
    base: css`
      font-family: 'ABC Social', 'Adjusted Arial Fallback', sans-serif;
    `,
    condensed: css`
      font-family: 'ABC Social Condensed', 'Adjusted Arial Narrow Fallback',
        sans-serif;
    `,
    mono: css`
      font-family: 'ABC Social Mono', 'Adjusted Courier New Fallback',
        monospaced;
    `,
    newSpirit: css`
      font-family: 'New Spirit Condensed', 'Adjusted Tahoma Fallback',
        sans-serif;
    `,
  },

  typography: {
    headingMastiff: css`
      ${props => props.theme.fontFamily.newSpirit}
      font-size: 5.25rem;
      font-style: normal;
      font-weight: 500;
      line-height: 86px;
      letter-spacing: -1.68px;
    `,
    heading1: css`
      ${props => props.theme.fontFamily.newSpirit}
      font-size: 2.875rem;
      font-style: normal;
      font-weight: 500;
      line-height: 48px;
      letter-spacing: -1.28px;

      @media ${props => props.theme.tablet} {
        font-size: 4rem;
        line-height: 66px;
      }
    `,
    heading2: css`
      ${props => props.theme.fontFamily.newSpirit}
      font-size: 3.375rem;
      font-style: normal;
      font-weight: 500;
      line-height: 56px;
      letter-spacing: -0.53px;
    `,
    heading3: css`
      ${props => props.theme.fontFamily.newSpirit}
      font-size: 2.25rem;
      font-style: normal;
      font-weight: 500;
      line-height: 40px;
      letter-spacing: 0.36px;
    `,
    heading4: css`
      ${props => props.theme.fontFamily.newSpirit}
      font-size: 1.5rem;
      font-style: normal;
      font-weight: 500;
      line-height: 30px;
    `,
    heading5: css`
      ${props => props.theme.fontFamily.newSpirit}
      font-size: 1.25rem;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
    `,
    heading6: css`
      ${props => props.theme.fontFamily.newSpirit}
      font-size: 1.25rem;
      font-style: normal;
      font-weight: 600;
      line-height: 24px;
      letter-spacing: 0.2px;
    `,
    subheading1: css`
      ${props => props.theme.fontFamily.mono}
      font-size: 1.125rem;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 2px;
      text-transform: uppercase;
    `,
    subheading2: css`
      ${props => props.theme.fontFamily.mono}
      font-size: 1rem;
      font-style: normal;
      font-weight: 400;
      line-height: 30px; /* 187.5% */
      letter-spacing: 1.6px;
      text-transform: uppercase;
    `,
    button: css`
      ${props => props.theme.fontFamily.base}
      font-size: 1.125rem;
      font-style: normal;
      font-weight: 700;
      line-height: 20px; /* 111.111% */
      letter-spacing: 0.18px;
    `,
    body: css`
      ${props => props.theme.fontFamily.base}
      font-size: 1rem;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 150% */
      letter-spacing: 0.16px;
    `,
    bodyBold: css`
      ${props => props.theme.fontFamily.base}
      font-size: 1rem;
      font-style: normal;
      font-weight: 700;
      line-height: 20px; /* 125% */
      letter-spacing: 0.16px;
    `,
    label: css`
      ${props => props.theme.fontFamily.base}
      font-size: 0.875rem;
      font-style: normal;
      font-weight: 400;
      line-height: 16px;
    `,
  },

  visuallyHidden: css`
    position: fixed;
    height: 1px;
    width: 1px;
    overflow: hidden;
    clip: rect(1px 1px 1px 1px); /* IE6, IE7 */
    clip: rect(1px, 1px, 1px, 1px);
    white-space: nowrap;
  `,
};

// Use this wrapped theme component instead of importing ThemeProvider and theme
export const OllieTheme = ({ children }) => (
  <StyleSheetManager enableVendorPrefixes>
    <ThemeProvider theme={theme}>{children}</ThemeProvider>
  </StyleSheetManager>
);

OllieTheme.propTypes = {
  children: PropTypes.node.isRequired,
};
